import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useMkRoot, useMkTheme } from "@/app/branding";

import { MkBreadcrumb } from "./MkBreadcrumb";
import { MkFabMenu } from "./MkFabMenu";
import { MkProfileModal } from "./MkProfileModal";
import { MkSidebar } from "./MkSidebar";
import type { Brand, Crumb, FabButton, NavItem, Personal } from "./types";

const HOME_ID = "__mk_home__";

interface Props {
    brand?: Brand;
    personal?: Personal;
    items?: NavItem[];
    active?: string;
    trail?: Crumb[];
    breadcrumb?: boolean;
    sidebar?: boolean;
    showFab?: boolean;
    /** takes the reader to a record of the application, when it has a screen for it */
    onOpenRecord?(model: string, id: number): void;
    fabExtra?: FabButton[];
    header?: ReactNode;
    sidebarExtra?: ReactNode;
    /** the application the reader is in; the menu opens its branch when it changes */
    sidebarSection?: string;
    onSelect?(id: string, item: NavItem | Crumb): void;
    onHome?(): void;
    onLogout?(): void;
    children: ReactNode;
}

export function MkLayout({ brand, personal, items = [], active, trail = [], breadcrumb = true,
                           sidebar = true, showFab = true, fabExtra = [], header, sidebarExtra, sidebarSection, onSelect,
                           onOpenRecord,
                           onHome, onLogout, children }: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const root = useMkRoot();
    const { theme, toggleSidebarMode } = useMkTheme();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);

    useEffect(() => {
        document.body.classList.add("mk-standalone", "o_mk_fullscreen");
        return () => document.body.classList.remove("o_mk_fullscreen");
    }, []);

    function goHome() {
        if (onHome) {
            onHome();
            return;
        }
        navigate("/");
    }

    function openProfile() {
        setMobileOpen(false);
        setProfileOpen(true);
    }

    const brandData: Brand = {
        id: brand?.id,
        label: brand?.label || "Mishkat",
        icon: brand?.icon || "fa-cube",
        logo: brand?.logo,
        onClick: brand?.onClick || goHome,
    };

    const personalData: Personal | undefined = personal
        ? { ...personal, onClick: openProfile }
        : undefined;

    const crumbs: Crumb[] = [
        { id: HOME_ID, label: t("mk.home"), icon: "fa-home", home: true },
        ...trail,
    ];

    const fabActions: FabButton[] = [...fabExtra];
    if (!sidebar) {
        fabActions.push({ id: "mk_profile", icon: "fa-user-circle-o", label: t("mk.profileSettings"),
                          variant: "info", onClick: openProfile });
        fabActions.push({ id: "mk_logout", icon: "fa-sign-out", label: t("mk.logOut"),
                          variant: "danger", onClick: () => onLogout?.() });
    }

    return (
        <div className={`mk mk-app mk-layout${mobileOpen ? " mk-layout-mobile-open" : ""}`} {...root}>
            <div className="mk-layout-scrim" onClick={() => setMobileOpen(false)} />
            {sidebar && (
                <div className="mk-layout-side">
                    <MkSidebar
                        brand={brandData}
                        personal={personalData}
                        items={items}
                        active={active}
                        collapsed={theme.sidebarMode === "icon"}
                        onSelect={(id, item) => {
                            setMobileOpen(false);
                            onSelect?.(id, item);
                        }}
                        onToggle={toggleSidebarMode}
                        onLogout={onLogout}
                        logoutLabel={t("mk.logOut")}
                        switcher={sidebarExtra}
                        section={sidebarSection}
                    />
                </div>
            )}
            <main className="mk-layout-main">
                <header className={`mk-layout-header${breadcrumb ? "" : " mk-layout-header-bare"}`}>
                    {sidebar && (
                        <button className="mk-layout-burger" onClick={() => setMobileOpen(true)}
                                aria-label={t("mk.openMenu")}>
                            <i className="fa fa-bars" />
                        </button>
                    )}
                    {breadcrumb && (
                        <MkBreadcrumb items={crumbs} className="mk-layout-crumbs"
                                      onSelect={(id, item) => {
                                          if (id === HOME_ID) {
                                              goHome();
                                              return;
                                          }
                                          onSelect?.(id, item);
                                      }} />
                    )}
                    <div className="mk-layout-header-actions">{header}</div>
                </header>
                <div className="mk-layout-content mk-anim-fade">{children}</div>
            </main>
            {showFab && <MkFabMenu extra={fabActions} openRecord={onOpenRecord} />}
            <MkProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
        </div>
    );
}
